import { gql } from "@apollo/client";

// queries
export const USERS = gql`
  query users {
    users {
      userId
      email
      fullName
      role
      status
      createdAt
      lastLogin
      city
      state
      zipCode
      billingEmail
      dbaName
      firmName
      cityId
      stateId
      countryId
      stripeAccount
      preferredReporterId
      liveLitigationLoginId
    }
  }
`;

export const PREFERRED_REPORTER_SEARCH = gql`
  query preferredReporterSearch {
    preferredReporterSearch {
      userId
      email
      fullName
      role
      status
      createdAt
      lastLogin
      city
      state
      zipCode
      billingEmail
      dbaName
      firmName
      cityId
      stateId
      countryId
      photoLink
      location
    }
  }
`;

export const GET_CONNECTED_LINK = gql`
  query getConnectedLink {
    getConnectedLink {
      connectedAccountLink
    }
  }
`;

export const GET_LL_LINK = gql`
  query getLiveLitigationLink {
    getLiveLitigationLink {
      liveLink
    }
  }
`;

export const USER_SEARCH = gql`
  query userSearch(
    $zipCode: String
    $city: String
    $name: String
    $start: String
    $id: String
  ) {
    userSearch(
      zipCode: $zipCode
      city: $city
      name: $name
      start: $start
      id: $id
    ) {
      userId
      email
      fullName
      role
      status
      createdAt
      lastLogin
      city
      state
      zipCode
      photoLink
      location
      isAvailable
      dbaName
      firmName
      cityId
      stateId
      countryId
      averageRating
      numRatings
      liveLitigationLoginId
    }
  }
`;

export const GET_UPLOAD_LINK = gql`
  query getUploadLink($file: String) {
    getUploadLink(file: $file) {
      link
    }
  }
`;

export const GET_DOWNLOAD_LINK = gql`
  query getDownloadLink($file: String) {
    getDownloadLink(file: $file) {
      link
    }
  }
`;

export const AVAILABILITY = gql`
  query availability {
    availability {
      availabilityId
      days
      start
      end
      userId
      daysOff
    }
  }
`;

export const JOBS = gql`
  query jobs {
    jobs {
      jobId
      userId
      csrUserId
      videoUserId
      start
      end
      status
      createdAt
      updatedAt
      reporterName
      requesterName
      transcriptTxt
      transcriptPdf
      orderForm
      name
      timeZone
      location
      witnessName
      additionalRequests
      interpreterLanguage
      insuranceCarrier
      claimNumber
      clientMatterNumber
      caseType
      witnessType
      bill_appearanceFee
      bill_hourlyRate
      bill_takeDownNoWrite
      bill_waitTime
      bill_afterHours
      bill_cancellationFee
      bill_medicalExpert
      bill_hearingsArbitrations
      bill_trials
      bill_videoTaped
      bill_audioTranscript
      bill_realtime
      bill_cart
      bill_roughDraft
      bill_etran
      bill_provideConferenceRoom
      bill_provideVideoConference
      bill_remoteServices
      bill_additionalLaptop
      bill_additionalTablet
      bill_originalTranscriptPlusOneCopy
      bill_indexFee
      bill_minimumOriginalTranscriptFee
      bill_copyTranscript
      bill_exhibitCopyBlackAndWhite
      bill_exhibitCopyColor
      bill_vtcSurcharge
      bill_sameDateExpedite
      bill_oneDayExpedite
      bill_twoDayExpedite
      bill_threeDayExpedite
      bill_fourDayExpedite
      bill_fiveDayExpedite
      bill_sixDayExpedite
      bill_sevenDayExpedite
      bill_eightDayExpedite
      bill_certificateOfNonappearance
      bill_courthouseHalfDay
      bill_courthouseFullDay
      bill_courthouseHourly
      bill_notaryPublic
      bill_deliveryHandling
      bill_mileage
      bill_videographerFee
      bill_videographerHourly
      invoiceId
      invoice
      serviceTotal
      pagesFromTranscript
      rating
      advancePayRequested
      requestedRealtime
      requestedRemote
      requestedVideographer
      requestedInterpreter
      meetingId
      meeting
    }
  }
`;

export const TRANSCRIPTS = gql`
  query transcripts {
    transcripts {
      jobId
      userId
      csrUserId
      transcriptId
      createdAt
      path
      numPages
    }
  }
`;

export const SERVICES = gql`
  query services($csrUserId: String, $videoUserId: String) {
    services(csrUserId: $csrUserId, videoUserId: $videoUserId) {
      servicesId
      userId
      appearanceFee
      hourlyRate
      takeDownNoWrite
      waitTime
      afterHours
      cancellationFee
      proBono
      medicalExpert
      hearingsArbitrations
      trials
      videoTaped
      audioTranscript
      realtime
      cart
      roughDraft
      etran
      provideConferenceRoom
      provideVideoConference
      remoteServices
      additionalLaptop
      additionalTablet
      originalTranscriptPlusOneCopy
      indexFee
      minimumOriginalTranscriptFee
      copyTranscript
      exhibitCopyBlackAndWhite
      exhibitCopyColor
      vtcSurcharge
      sameDateExpedite
      oneDayExpedite
      twoDayExpedite
      threeDayExpedite
      fourDayExpedite
      fiveDayExpedite
      sixDayExpedite
      sevenDayExpedite
      eightDayExpedite
      certificateOfNonappearance
      courthouseHalfDay
      courthouseFullDay
      courthouseHourly
      notaryPublic
      deliveryHandling
      mileage
      videographerFee
      videographerHourly
    }
  }
`;

export const ORDER_FORMS = gql`
  query orderForms {
    orderForms {
      jobId
      userId
      csrUserId
      orderFormId
      createdAt
      path
    }
  }
`;

export const FILES = gql`
  query files($jobId: String, $type: String, $userId: String) {
    files(jobId: $jobId, type: $type, userId: $userId) {
      fileId
      userId
      jobId
      type
      path
      filename
      meta
      createdAt
      updatedAt
    }
  }
`;

// mutations

export const CREATE_PASS = gql`
  mutation createPass($email: String, $phone: String) {
    createPass(email: $email, phone: $phone) {
      status
    }
  }
`;

export const VALIDATE_PASS = gql`
  mutation validatePass($email: String, $phone: String, $passcode: String!) {
    validatePass(email: $email, phone: $phone, passcode: $passcode) {
      status
      token
      message
    }
  }
`;

export const CREATE_USER = gql`
  mutation createUser(
    $email: String!
    $phone: String
    $role: String!
    $fullName: String
    $city: String
    $state: String
    $zipCode: String
    $billingEmail: String
    $dbaName: String
    $firmName: String
    $cityId: String
    $stateId: String
    $countryId: String
    $isInvite: Boolean
    $invitorName: String
    $preferredReporterId: String
  ) {
    createUser(
      userInfo: {
        email: $email
        phone: $phone
        role: $role
        fullName: $fullName
        city: $city
        state: $state
        zipCode: $zipCode
        billingEmail: $billingEmail
        dbaName: $dbaName
        firmName: $firmName
        cityId: $cityId
        stateId: $stateId
        countryId: $countryId
        isInvite: $isInvite
        invitorName: $invitorName
        preferredReporterId: $preferredReporterId
      }
    ) {
      status
    }
  }
`;

export const CREATE_USERS = gql`
  mutation createUsers($userInfo: [AddUserInfo]) {
    createUsers(userInfo: $userInfo) {
      status
    }
  }
`;

export const UPDATE_USER = gql`
  mutation updateUser(
    $userId: String!
    $email: String
    $phone: String
    $fullName: String
    $role: String
    $status: String
    $city: String
    $state: String
    $zipCode: String
    $street1: String
    $street2: String
    $billingEmail: String
    $dbaName: String
    $firmName: String
    $cityId: String
    $stateId: String
    $countryId: String
    $preferredReporterId: String
  ) {
    updateUser(
      userInfo: {
        userId: $userId
        email: $email
        phone: $phone
        fullName: $fullName
        role: $role
        status: $status
        city: $city
        state: $state
        zipCode: $zipCode
        street1: $street1
        street2: $street2
        billingEmail: $billingEmail
        dbaName: $dbaName
        firmName: $firmName
        cityId: $cityId
        stateId: $stateId
        countryId: $countryId
        preferredReporterId: $preferredReporterId
      }
    ) {
      status
      message
      token
    }
  }
`;

export const CREATE_AVAILABILITY = gql`
  mutation createAvailability(
    $days: String
    $start: String
    $end: String
    $daysOff: String
  ) {
    createAvailability(
      availabilityInfo: {
        days: $days
        start: $start
        end: $end
        daysOff: $daysOff
      }
    ) {
      status
      message
    }
  }
`;

export const UPDATE_AVAILABILITY = gql`
  mutation updateAvailability(
    $availabilityId: String!
    $days: String
    $start: String
    $end: String
    $daysOff: String
  ) {
    updateAvailability(
      availabilityInfo: {
        availabilityId: $availabilityId
        days: $days
        start: $start
        end: $end
        daysOff: $daysOff
      }
    ) {
      status
      message
    }
  }
`;

export const CREATE_JOB = gql`
  mutation createJob(
    $start: String
    $end: String
    $csrUserId: String
    $status: String
    $name: String
    $timeZone: String
    $location: String
    $clientMatterNumber: String
    $witnessName: String
    $additionalRequests: String
    $interpreterLanguage: String
    $insuranceCarrier: String
    $claimNumber: String
    $caseType: String
    $witnessType: String
    $requestedRealtime: Boolean
    $requestedRemote: Boolean
    $requestedVideographer: Boolean
    $requestedInterpreter: Boolean
  ) {
    createJob(
      jobInfo: {
        start: $start
        end: $end
        csrUserId: $csrUserId
        status: $status
        name: $name
        timeZone: $timeZone
        location: $location
        caseType: $caseType
        clientMatterNumber: $clientMatterNumber
        insuranceCarrier: $insuranceCarrier
        claimNumber: $claimNumber
        interpreterLanguage: $interpreterLanguage
        additionalRequests: $additionalRequests
        witnessName: $witnessName
        witnessType: $witnessType
        requestedRealtime: $requestedRealtime
        requestedRemote: $requestedRemote
        requestedVideographer: $requestedVideographer
        requestedInterpreter: $requestedInterpreter
      }
    ) {
      status
      jobId
      message
    }
  }
`;

export const UPDATE_JOB = gql`
  mutation updateJob(
    $jobId: String!
    $start: String
    $end: String
    $csrUserId: String
    $status: String
    $name: String
    $timeZone: String
    $location: String
    $caseType: String
    $clientMatterNumber: String
    $insuranceCarrier: String
    $claimNumber: String
    $interpreterLanguage: String
    $additionalRequests: String
    $witnessName: String
    $witnessType: String
    $bill_appearanceFee: Boolean
    $bill_hourlyRate: String
    $bill_takeDownNoWrite: Boolean
    $bill_waitTime: String
    $bill_afterHours: String
    $bill_cancellationFee: Boolean
    $bill_medicalExpert: String
    $bill_hearingsArbitrations: String
    $bill_trials: String
    $bill_videoTaped: String
    $bill_audioTranscript: String
    $bill_realtime: String
    $bill_cart: String
    $bill_roughDraft: String
    $bill_etran: String
    $bill_provideConferenceRoom: String
    $bill_provideVideoConference: String
    $bill_remoteServices: Boolean
    $bill_additionalLaptop: String
    $bill_additionalTablet: String
    $bill_originalTranscriptPlusOneCopy: String
    $bill_indexFee: String
    $bill_minimumOriginalTranscriptFee: Boolean
    $bill_copyTranscript: String
    $bill_exhibitCopyBlackAndWhite: String
    $bill_exhibitCopyColor: String
    $bill_vtcSurcharge: String
    $bill_sameDateExpedite: String
    $bill_oneDayExpedite: String
    $bill_twoDayExpedite: String
    $bill_threeDayExpedite: String
    $bill_fourDayExpedite: String
    $bill_fiveDayExpedite: String
    $bill_sixDayExpedite: String
    $bill_sevenDayExpedite: String
    $bill_eightDayExpedite: String
    $bill_certificateOfNonappearance: Boolean
    $bill_courthouseHalfDay: Boolean
    $bill_courthouseFullDay: Boolean
    $bill_courthouseHourly: String
    $bill_notaryPublic: String
    $bill_deliveryHandling: Boolean
    $bill_mileage: String
    $bill_videographerFee: Boolean
    $bill_videographerHourly: String
    $updatedAt: String
    $isGenerateInvoice: Boolean
    $userId: String
    $serviceTotal: String
    $advancePayRequested: Boolean
    $rating: String
    $requestedRealtime: Boolean
    $requestedRemote: Boolean
    $requestedVideographer: Boolean
    $requestedInterpreter: Boolean
    $newAttendees: String
  ) {
    updateJob(
      jobInfo: {
        jobId: $jobId
        start: $start
        end: $end
        csrUserId: $csrUserId
        status: $status
        name: $name
        timeZone: $timeZone
        location: $location
        caseType: $caseType
        clientMatterNumber: $clientMatterNumber
        claimNumber: $claimNumber
        insuranceCarrier: $insuranceCarrier
        interpreterLanguage: $interpreterLanguage
        additionalRequests: $additionalRequests
        witnessName: $witnessName
        witnessType: $witnessType
        bill_appearanceFee: $bill_appearanceFee
        bill_hourlyRate: $bill_hourlyRate
        bill_takeDownNoWrite: $bill_takeDownNoWrite
        bill_waitTime: $bill_waitTime
        bill_afterHours: $bill_afterHours
        bill_cancellationFee: $bill_cancellationFee
        bill_medicalExpert: $bill_medicalExpert
        bill_hearingsArbitrations: $bill_hearingsArbitrations
        bill_trials: $bill_trials
        bill_videoTaped: $bill_videoTaped
        bill_audioTranscript: $bill_audioTranscript
        bill_realtime: $bill_realtime
        bill_cart: $bill_cart
        bill_roughDraft: $bill_roughDraft
        bill_etran: $bill_etran
        bill_provideConferenceRoom: $bill_provideConferenceRoom
        bill_provideVideoConference: $bill_provideVideoConference
        bill_remoteServices: $bill_remoteServices
        bill_additionalLaptop: $bill_additionalLaptop
        bill_additionalTablet: $bill_additionalTablet
        bill_originalTranscriptPlusOneCopy: $bill_originalTranscriptPlusOneCopy
        bill_indexFee: $bill_indexFee
        bill_minimumOriginalTranscriptFee: $bill_minimumOriginalTranscriptFee
        bill_copyTranscript: $bill_copyTranscript
        bill_exhibitCopyBlackAndWhite: $bill_exhibitCopyBlackAndWhite
        bill_exhibitCopyColor: $bill_exhibitCopyColor
        bill_vtcSurcharge: $bill_vtcSurcharge
        bill_sameDateExpedite: $bill_sameDateExpedite
        bill_oneDayExpedite: $bill_oneDayExpedite
        bill_twoDayExpedite: $bill_twoDayExpedite
        bill_threeDayExpedite: $bill_threeDayExpedite
        bill_fourDayExpedite: $bill_fourDayExpedite
        bill_fiveDayExpedite: $bill_fiveDayExpedite
        bill_sixDayExpedite: $bill_sixDayExpedite
        bill_sevenDayExpedite: $bill_sevenDayExpedite
        bill_eightDayExpedite: $bill_eightDayExpedite
        bill_certificateOfNonappearance: $bill_certificateOfNonappearance
        bill_courthouseHalfDay: $bill_courthouseHalfDay
        bill_courthouseFullDay: $bill_courthouseFullDay
        bill_courthouseHourly: $bill_courthouseHourly
        bill_notaryPublic: $bill_notaryPublic
        bill_deliveryHandling: $bill_deliveryHandling
        bill_mileage: $bill_mileage
        bill_videographerFee: $bill_videographerFee
        bill_videographerHourly: $bill_videographerHourly
        updatedAt: $updatedAt
        isGenerateInvoice: $isGenerateInvoice
        userId: $userId
        serviceTotal: $serviceTotal
        advancePayRequested: $advancePayRequested
        rating: $rating
        requestedRealtime: $requestedRealtime
        requestedRemote: $requestedRemote
        requestedVideographer: $requestedVideographer
        requestedInterpreter: $requestedInterpreter
        newAttendees: $newAttendees
      }
    ) {
      status
      message
    }
  }
`;

export const CREATE_TRANSCRIPT = gql`
  mutation createTranscript(
    $jobId: String
    $csrUserId: String
    $userId: String
    $path: String
    $type: String
    $numPages: String
  ) {
    createTranscript(
      transcriptInfo: {
        jobId: $jobId
        csrUserId: $csrUserId
        userId: $userId
        path: $path
        type: $type
        numPages: $numPages
      }
    ) {
      status
      transcriptId
      message
    }
  }
`;

export const CREATE_ORDER_FORM = gql`
  mutation createOrderForm(
    $jobId: String
    $csrUserId: String
    $userId: String
    $path: String
  ) {
    createOrderForm(
      orderFormInfo: {
        jobId: $jobId
        csrUserId: $csrUserId
        userId: $userId
        path: $path
      }
    ) {
      status
      orderFormId
      message
    }
  }
`;

export const UPDATE_SERVICES = gql`
  mutation updateServices(
    $appearanceFee: String
    $hourlyRate: String
    $takeDownNoWrite: String
    $waitTime: String
    $afterHours: String
    $cancellationFee: String
    $proBono: Boolean
    $medicalExpert: String
    $hearingsArbitrations: String
    $trials: String
    $videoTaped: String
    $audioTranscript: String
    $realtime: String
    $cart: String
    $roughDraft: String
    $etran: String
    $provideConferenceRoom: String
    $provideVideoConference: String
    $remoteServices: String
    $additionalLaptop: String
    $additionalTablet: String
    $originalTranscriptPlusOneCopy: String
    $indexFee: String
    $minimumOriginalTranscriptFee: String
    $copyTranscript: String
    $exhibitCopyBlackAndWhite: String
    $exhibitCopyColor: String
    $vtcSurcharge: String
    $sameDateExpedite: String
    $oneDayExpedite: String
    $twoDayExpedite: String
    $threeDayExpedite: String
    $fourDayExpedite: String
    $fiveDayExpedite: String
    $sixDayExpedite: String
    $sevenDayExpedite: String
    $eightDayExpedite: String
    $certificateOfNonappearance: String
    $courthouseHalfDay: String
    $courthouseFullDay: String
    $courthouseHourly: String
    $notaryPublic: String
    $deliveryHandling: String
    $mileage: String
    $videographerFee: String
    $videographerHourly: String
  ) {
    updateServices(
      servicesInfo: {
        appearanceFee: $appearanceFee
        hourlyRate: $hourlyRate
        takeDownNoWrite: $takeDownNoWrite
        waitTime: $waitTime
        afterHours: $afterHours
        cancellationFee: $cancellationFee
        proBono: $proBono
        medicalExpert: $medicalExpert
        hearingsArbitrations: $hearingsArbitrations
        trials: $trials
        videoTaped: $videoTaped
        audioTranscript: $audioTranscript
        realtime: $realtime
        cart: $cart
        roughDraft: $roughDraft
        etran: $etran
        provideConferenceRoom: $provideConferenceRoom
        provideVideoConference: $provideVideoConference
        remoteServices: $remoteServices
        additionalLaptop: $additionalLaptop
        additionalTablet: $additionalTablet
        originalTranscriptPlusOneCopy: $originalTranscriptPlusOneCopy
        indexFee: $indexFee
        minimumOriginalTranscriptFee: $minimumOriginalTranscriptFee
        copyTranscript: $copyTranscript
        exhibitCopyBlackAndWhite: $exhibitCopyBlackAndWhite
        exhibitCopyColor: $exhibitCopyColor
        vtcSurcharge: $vtcSurcharge
        sameDateExpedite: $sameDateExpedite
        oneDayExpedite: $oneDayExpedite
        twoDayExpedite: $twoDayExpedite
        threeDayExpedite: $threeDayExpedite
        fourDayExpedite: $fourDayExpedite
        fiveDayExpedite: $fiveDayExpedite
        sixDayExpedite: $sixDayExpedite
        sevenDayExpedite: $sevenDayExpedite
        eightDayExpedite: $eightDayExpedite
        certificateOfNonappearance: $certificateOfNonappearance
        courthouseHalfDay: $courthouseHalfDay
        courthouseFullDay: $courthouseFullDay
        courthouseHourly: $courthouseHourly
        notaryPublic: $notaryPublic
        deliveryHandling: $deliveryHandling
        mileage: $mileage
        videographerFee: $videographerFee
        videographerHourly: $videographerHourly
      }
    ) {
      status
      message
    }
  }
`;

export const CREATE_FILE = gql`
  mutation createFile(
    $userId: String!
    $jobId: String
    $type: String!
    $path: String!
    $filename: String
    $meta: String
  ) {
    createFile(
      fileInfo: {
        userId: $userId
        jobId: $jobId
        type: $type
        path: $path
        filename: $filename
        meta: $meta
      }
    ) {
      status
      fileId
      message
    }
  }
`;

// Google Calendar integration

// 1. Query: Is user connected?
export const GOOGLE_CALENDAR_CONNECTION_STATUS = gql`
  query googleCalendarConnectionStatus {
    googleCalendarConnectionStatus
  }
`;

// 2. Mutation: Get Google Auth URL (redirect user here)
export const GET_GOOGLE_CALENDAR_AUTH_URL = gql`
  mutation getGoogleCalendarAuthUrl {
    getGoogleCalendarAuthUrl
  }
`;

// 3. Mutation: Handle Google OAuth callback (code from URL)
export const HANDLE_GOOGLE_CALENDAR_OAUTH = gql`
  mutation handleGoogleCalendarOAuth($code: String!) {
    handleGoogleCalendarOAuth(code: $code)
  }
`;

// 4. Mutation: Disconnect
export const DISCONNECT_GOOGLE_CALENDAR = gql`
  mutation disconnectGoogleCalendar {
    disconnectGoogleCalendar
  }
`;
